import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowRight,
  BanknoteArrowDown,
  ChartPie,
  Check,
  CircleCheck,
  Copy,
  Eye,
  EyeOff,
  FileText,
  Info,
  RotateCcw,
  Tag,
  SquareArrowOutUpRight,
  Wallet,
  X,
} from "lucide-react";
import Countdown from "../components/Countdown";
import ShareModal from "../components/ShareModal";
import IosSpinner from "../components/IosSpinner";
import DiscordIcon from "../icons/DiscordIcon";
import styles from "./PortfolioPage.module.css";
import { useLanguage } from "../LanguageContext";
import useAccountMetrics from "../useAccountMetrics";
import useWithdrawals from "../useWithdrawals";
import usePayments from "../usePayments";
import { useLeaderboard, useStats } from "../usePlatformData";

function WindowsIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.549H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-13.051-1.849" />
    </svg>
  );
}

function AppleIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.54 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  );
}

function MT5DownloadModal({ onClose }) {
  const { t } = useLanguage();
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleWindowsDownload = () => {
    window.location.href = "https://prop-trading-fun.s3.us-west-1.amazonaws.com/tcbridge5setup.exe";
  };

  const handleMacDownload = () => {
    window.location.href = "https://prop-trading-fun.s3.us-west-1.amazonaws.com/MetaTrader5.pkg";
  };

  return createPortal(
    <div className={styles.mt5Overlay} onMouseDown={onClose}>
      <div
        className={styles.mt5Modal}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t("port_mt5_download")}
      >
        <button type="button" className={styles.mt5Close} onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>
        <h2 className={styles.mt5Title}>{t("port_mt5_download")}</h2>
        <div className={styles.mt5BtnRow}>
          <button type="button" className={styles.mt5OsBtn} onClick={handleWindowsDownload}>
            <WindowsIcon size={22} />
            Windows
          </button>
          <button type="button" className={styles.mt5OsBtn} onClick={handleMacDownload}>
            <AppleIcon size={22} />
            Mac
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function CopyBtn({ label, value }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = () => {
    if (value == null || !navigator.clipboard) return;
    navigator.clipboard.writeText(String(value)).then(() => setCopied(true), () => {});
  };

  return (
    <button
      type="button"
      className={styles.iconBtn}
      aria-label={`Copy ${label}`}
      onClick={handleCopy}
    >
      {copied ? <Check size={16} /> : <Copy size={16} />}
    </button>
  );
}

function PasswordField({ label, value }) {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  return (
    <div className={styles.accountRow}>
      <span className={styles.accountLabel}>{label}</span>
      <span className={styles.accountValue}>
        <span>{visible ? value : "******"}</span>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? t("port_hide_pw") : t("port_show_pw")}
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
        <CopyBtn label={label} value={value} />
      </span>
    </div>
  );
}

// Stand-in credentials for the previews (state switcher, funded stages) where no
// account has been assigned
const SAMPLE_MT5_ACCOUNT = {
  login: "54321",
  masterPassword: "mypassword123",
  investorPassword: "mypassword123",
  server: "TCBRIDGE-DEMO",
};

function Mt5DetailsCard({ account, children }) {
  const { t } = useLanguage();
  const [mt5Open, setMt5Open] = useState(false);
  const { login, masterPassword, investorPassword, server } = account || SAMPLE_MT5_ACCOUNT;
  return (
    <>
      <p className={styles.sectionLabel}>{t("port_mt5_account_details")}</p>
      <article className={styles.sideCard}>
        <div className={styles.accountRows}>
          <div className={styles.accountRow}>
            <span className={styles.accountLabel}>{t("port_login")}</span>
            <span className={styles.accountValue}>
              <span>{login}</span>
              <CopyBtn label="login" value={login} />
            </span>
          </div>
          <PasswordField label={t("port_master_pw")} value={masterPassword} />
          <PasswordField label={t("port_investor_pw")} value={investorPassword} />
          <div className={styles.accountRow}>
            <span className={styles.accountLabel}>{t("port_mt5_server")}</span>
            <span className={styles.accountValue}>
              <span className={styles.mt5ServerLink} onClick={() => setMt5Open(true)}>{server}</span>
              <button type="button" className={styles.iconBtn} aria-label="Download MT5" onClick={() => setMt5Open(true)}>
                <ArrowDownToLine size={16} />
              </button>
            </span>
          </div>
        </div>
        {children}
      </article>
      {mt5Open && <MT5DownloadModal onClose={() => setMt5Open(false)} />}
    </>
  );
}

function InfoTooltip({ text, size = 14 }) {
  const [visible, setVisible] = useState(false);
  return (
    <span
      className={styles.tooltipWrap}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <Info size={size} className={styles.infoIcon} />
      {visible && <span className={styles.tooltip}>{text}</span>}
    </span>
  );
}

function ProgressTile({ label, tooltip, fill, currentText, targetText, targetIcon, tone }) {
  const toneClass = styles[`progress${tone[0].toUpperCase()}${tone.slice(1)}`];
  return (
    <article className={`${styles.overviewTile} ${styles.progressTile} ${toneClass}`}>
      <p className={styles.tileLabel}>
        {label} <InfoTooltip text={tooltip} />
      </p>
      <div className={styles.progressTrack}>
        <span className={styles.progressFill} style={{ width: fill }} />
      </div>
      <div className={styles.progressMeta}>
        <span className={styles.progressCurrent}>{currentText}</span>
        <span className={styles.progressTarget}>
          {targetText}
          {targetIcon}
        </span>
      </div>
    </article>
  );
}

const blurredTiles = [
  { labelKey: "port_balance", value: "10,000 PRP" },
  { labelKey: "port_profit_loss", value: "+0 PRP" },
  { labelKey: "port_rank", value: "–" },
  { labelKey: "port_inactivity", value: "0 days" },
];

// "—" until the figure loads, or while the platform doesn't track it yet (same as the Stats page)
const formatStat = (value, prefix = "") =>
  value == null ? "—" : `${prefix}${Math.round(value).toLocaleString("en-US")}`;

// Live figures from GET /api/stats
const platformStatsOf = (overview) => [
  { labelKey: "port_free_accounts_stat", tooltipKey: "port_tooltip_free_accounts", value: formatStat(overview?.freeAccounts) },
  { labelKey: "port_qualified_stat", tooltipKey: "port_tooltip_qualified", value: formatStat(overview?.qualified) },
  { labelKey: "port_activated_stat", tooltipKey: "port_tooltip_activated", value: formatStat(overview?.activated) },
  { labelKey: "port_total_paid_stat", tooltipKey: "port_tooltip_total_paid", value: formatStat(overview?.usdcPaid, "$") },
];

const processSteps = [
  { n: 1, titleKey: "port_step1_title", descKey: "port_step1_desc" },
  { n: 2, titleKey: "port_step2_title", descKey: "port_step2_desc" },
  { n: 3, titleKey: "port_step3_title", descKey: "port_step3_desc" },
  { n: 4, titleKey: "port_step4_title", descKey: "port_step4_desc" },
];

// `notice`: a status line under the button, e.g. while waiting for an account
function FreeUserView({ onStartTrading, activating, error, notice }) {
  const { t } = useLanguage();
  const { data: stats } = useStats();

  return (
    <section className={styles.page}>
      <h1 className={styles.heroTitle}>{t("port_title")}</h1>

      {/* Overview — blurred tiles + start trading overlay */}
      <section className={styles.section}>
        <p className={styles.sectionLabel}>{t("port_overview")}</p>
        <div className={styles.freeOverviewWrap}>
          <div
            className={`${styles.overviewGrid} ${styles.overviewBlurred}`}
            aria-hidden="true"
          >
            {blurredTiles.map((tile) => (
              <article key={tile.labelKey} className={styles.overviewTile}>
                <p className={styles.tileLabel}>{t(tile.labelKey)}</p>
                <strong className={styles.tileValue}>{tile.value}</strong>
              </article>
            ))}
          </div>
          <div className={styles.startCard}>
            <p className={styles.startTitle}>{t("port_free_title")}</p>
            <p className={styles.startDesc}>{t("port_free_desc")}</p>
            <button
              type="button"
              className={styles.startBtn}
              onClick={onStartTrading}
              disabled={activating}
            >
              {t("port_start_trading")}
            </button>
            {error && (
              <p className={styles.startError} role="alert">
                {error}
              </p>
            )}
            {!error && notice && (
              <p className={styles.startNotice} role="status">
                {notice}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* About */}
      <section className={styles.section}>
        <h2 className={styles.sectionLabelAbout}>{t("port_about_title")}</h2>
      </section>

      {/* Trading Objectives */}
      <section className={styles.section}>
        <p className={styles.sectionLabel}>{t("port_trading_obj")}</p>
        <div className={styles.objectivesGrid}>
          {[
            { label: t("port_max_drawdown"), pct: "20%", desc: "", tooltip: t("port_tooltip_max_drawdown") },
            { label: t("port_daily_loss"), pct: "5%", desc: t("port_of_starting"), tooltip: t("port_tooltip_daily_loss") },
          ].map((obj) => (
            <article key={obj.label} className={styles.objectiveCard}>
              <p className={styles.objectiveLabel}>
                {obj.label} <InfoTooltip text={obj.tooltip} size={13} />
              </p>
              <p className={styles.objectiveValueRow}>
                <strong className={styles.objectivePct}>{obj.pct}</strong>
                {obj.desc && <span className={styles.objectiveDesc}>{obj.desc}</span>}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Platform Stats */}
      <section className={styles.section}>
        <p className={styles.sectionLabel}>{t("port_platform_stats")}</p>
        <div className={`${styles.overviewGrid} ${styles.platformStatsGrid}`}>
          {platformStatsOf(stats?.overview).map((s) => (
            <article key={s.labelKey} className={styles.overviewTile}>
              <p className={styles.tileLabel}>
                {t(s.labelKey)} <InfoTooltip text={t(s.tooltipKey)} />
              </p>
              <strong className={styles.tileValue}>{s.value}</strong>
            </article>
          ))}
        </div>
      </section>

      {/* Real Traders & Real Payouts */}
      <section className={styles.section}>
        <p className={styles.sectionLabel}>{t("port_real_payouts_title")}</p>
        {/* Trader USDC payouts aren't tracked by the API yet; cards go here once they are */}
        <div className={styles.payoutsGrid}>
          <p className={`${styles.payoutsEmpty} ${styles.payoutTime}`}>{t("stats_no_payouts")}</p>
        </div>
      </section>
    </section>
  );
}

// Loading overlay over a blurred copy of the page the trader started from
function ActivatingView({ children }) {
  const { t } = useLanguage();

  return (
    <div className={styles.activatingWrap}>
      <div className={styles.activatingBlur} aria-hidden="true">
        {children}
      </div>
      <div className={styles.activatingOverlay} role="status" aria-live="polite">
        <IosSpinner size={52} />
        <p className={styles.activatingText}>{t("port_getting_ready")}</p>
      </div>
    </div>
  );
}

const ACTIVATION_FEE = "$249";
// Under this much time left, the activation banner turns gold and the copy turns urgent
const URGENT_THRESHOLD_MS = 24 * 60 * 60 * 1000;

const LANG_LOCALE = { EN: "en-US", ES: "es-ES", PT: "pt-BR", FR: "fr-FR", AR: "ar-EG" };

// True once the `at` timestamp has passed; flips on its own when that moment arrives
function useHasPassed(at) {
  const [passed, setPassed] = useState(() => Date.now() >= at);

  useEffect(() => {
    const remaining = at - Date.now();
    setPassed(remaining <= 0);
    if (remaining <= 0) return undefined;
    const timer = setTimeout(() => setPassed(true), remaining);
    return () => clearTimeout(timer);
  }, [at]);

  return passed;
}

// Activation window ran out: the qualification can't be activated, only a new free attempt
// (or, with none left, the paid activation via `onBuy`)
function ActivationExpiredBanner({ expiredAt, onRetry, onBuy, error }) {
  const { t, langCode } = useLanguage();
  const locale = LANG_LOCALE[langCode] ?? "en-US";
  const expiredText = t("port_expired_on")
    .replace("{date}", new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric" }).format(expiredAt))
    .replace("{time}", new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(expiredAt));

  return (
    <section className={`${styles.section} ${styles.activationBanner} ${styles.activationExpired}`} role="alert">
      <div className={styles.activationMain}>
        <h2 className={styles.activationHeading}>
          {t("port_window_title")}{" "}
          <span className={styles.processHighlight}>{t("port_window_expired")}</span>
        </h2>
        <p className={styles.activationDesc}>{t("port_window_expired_desc")}</p>
        <div className={`${styles.activationStats} ${styles.activationStatsThree}`}>
          <div className={styles.activationStat}>
            <p className={styles.tileLabel}>{t("port_qualification_result")}</p>
            <strong className={styles.tileValue}>{t("port_qualification_reached")}</strong>
          </div>
          <div className={styles.activationStat}>
            <p className={styles.tileLabel}>{t("port_activation_fee")}</p>
            <strong className={styles.tileValue}>{ACTIVATION_FEE}</strong>
          </div>
          <div className={styles.activationStat}>
            <p className={styles.tileLabel}>{t("port_qualification_redemption")}</p>
            <strong className={styles.tileValue}>{t("port_expired_value")}</strong>
          </div>
        </div>
      </div>

      <div className={styles.activationSide}>
        <p className={styles.tileLabel}>{t("port_activation_status")}</p>
        <strong className={styles.expiredStatus}>{t("port_expired_status")}</strong>
        <p className={styles.expiredAt}>{expiredText}</p>
        <button type="button" className={styles.startBtn} onClick={onBuy ?? onRetry}>
          {onBuy ? `${t("port_get_funded_for")} ${ACTIVATION_FEE}` : t("port_start_next_attempt")}{" "}
          <ArrowRight size={18} strokeWidth={2.4} />
        </button>
        {error && <p className={styles.startError}>{error}</p>}
      </div>
    </section>
  );
}

function ActivationBanner({ deadline, onActivate, onRetry, onBuy, error }) {
  const { t } = useLanguage();
  const urgent = useHasPassed(deadline - URGENT_THRESHOLD_MS);
  const expired = useHasPassed(deadline);

  if (expired) {
    return <ActivationExpiredBanner expiredAt={deadline} onRetry={onRetry} onBuy={onBuy} error={error} />;
  }

  return (
    <section className={`${styles.section} ${styles.activationBanner} ${urgent ? styles.activationUrgent : ""}`}>
      <div className={styles.activationMain}>
        <h2 className={styles.activationHeading}>
          {t(urgent ? "port_activate_soon" : "port_hit_target")}{" "}
          <span className={styles.processHighlight}>
            {t(urgent ? "port_time_running_out" : "port_time_to_activate")}
          </span>
        </h2>
        <p className={styles.activationDesc}>{t(urgent ? "port_activate_urgent_desc" : "port_activate_desc")}</p>
        <div className={styles.activationStats}>
          <div className={styles.activationStat}>
            <p className={styles.tileLabel}>{t("port_activation_fee")}</p>
            <strong className={styles.tileValue}>{ACTIVATION_FEE}</strong>
          </div>
          <div className={styles.activationStat}>
            <p className={styles.tileLabel}>
              <span className={styles.desktopOnly}>{t("port_redeem_after_activation")}</span>
              <span className={styles.mobileOnly}>{t("port_redeem_after_activation_short")}</span>
            </p>
            <strong className={styles.tileValue}>
              {QUALIFICATION_PROFIT_PRP * PRP_RATE} USDC <span className={styles.activationSub}>({QUALIFICATION_PROFIT_PRP} PRP)</span>
            </strong>
          </div>
        </div>
      </div>

      <div className={styles.activationSide}>
        <p className={styles.tileLabel}>{t("port_time_left")}</p>
        <Countdown deadline={deadline} className={styles.bannerCountdown} />
        <button type="button" className={styles.startBtn} onClick={onActivate}>
          {t("port_activate_short")}
          <ArrowRight size={18} strokeWidth={2.4} />
        </button>
      </div>
    </section>
  );
}

// Free-account rules that fail an attempt, in the order the banner names them
// (the API's src/config/breaches.js words its emails the same way)
const BREACH_RULE_ORDER = ["max_drawdown", "daily_loss", "inactivity"];

// "a", "a & b", "a, b & c"
const joinTitles = (parts) =>
  parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} & ${parts[parts.length - 1]}`;
// "a", "a and b", "a, b, and c"
const joinNames = (parts, and) =>
  parts.length < 3
    ? parts.join(` ${and} `)
    : `${parts.slice(0, -1).join(", ")}, ${and} ${parts[parts.length - 1]}`;

// Banner wording naming every breached rule, e.g.
//   title "Max drawdown & daily loss breached"
//   desc  "Your account exceeded the maximum drawdown and daily loss limit."
function breachCopy(t, failedRules) {
  const rules = BREACH_RULE_ORDER.filter((r) => failedRules.includes(r));
  const titles = rules.map((r, i) => {
    const title = t(`port_rule_title_${r}`);
    return i === 0 ? title : title.toLowerCase();
  });
  const title = `${joinTitles(titles)} ${t("port_breached")}`;
  const desc =
    rules.length === 1 && rules[0] === "inactivity"
      ? t("port_breach_desc_inactivity")
      : `${t("port_breach_desc_prefix")} ${joinNames(rules.map((r) => t(`port_rule_name_${r}`)), t("port_and"))}.`;
  return { title, desc };
}

function BreachBanner({ failedRules, onRetry, error }) {
  const { t } = useLanguage();
  const { title, desc } = breachCopy(t, failedRules);
  return (
    <section className={`${styles.section} ${styles.breachBanner}`} role="alert">
      <div>
        <h2 className={styles.breachHeading}>{title}</h2>
        <p className={styles.breachDesc}>
          {desc} {t("port_breach_retry_left")}
        </p>
        {error && <p className={styles.startError}>{error}</p>}
      </div>
      <button type="button" className={styles.retryBtn} onClick={onRetry}>
        {t("port_free_retry")} <ArrowRight size={20} strokeWidth={2.4} />
      </button>
    </section>
  );
}

function ExhaustedBanner({ failedRules, onBuy }) {
  const { t } = useLanguage();
  const { title } = breachCopy(t, failedRules);
  return (
    <section className={`${styles.section} ${styles.breachBanner}`} role="alert">
      <div>
        <h2 className={styles.breachHeading}>{t("port_exhausted_title")}</h2>
        <p className={styles.breachDesc}>
          {title}. {t("port_exhausted_desc")} <br className={styles.desktopBreak} />
          {t("port_exhausted_offer")} <strong className={styles.breachPrice}>{ACTIVATION_FEE}</strong>.
        </p>
        <p className={styles.promoLine}>
          <Tag size={20} aria-hidden="true" />
          <span>
            {t("port_promo_prefix")} <strong className={styles.promoCode}>FREEENTRY</strong>{" "}
            {t("port_promo_suffix")}
          </span>
        </p>
      </div>
      <button type="button" className={styles.retryBtn} onClick={onBuy}>
        {t("port_get_funded")} <ArrowRight size={20} strokeWidth={2.4} />
      </button>
    </section>
  );
}

/* ── Post-activation (KYC pending / verified) ── */

const QUALIFICATION_PROFIT_PRP = 100;
const PRP_RATE = 0.5;
const PROCESSING_FEE_RATE = 0.02;

// Funded payouts: withdraw between 2% of the account and the available profit
const PAYOUT_AVAILABLE_PRP = 1000;
const PAYOUT_MIN_PRP = 200;

function formatNumber(n) {
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

// Balance and profit/loss tiles: whole PRP, as in Figma, e.g. 10,001 (and never "-0")
function formatPrp(n) {
  return (Math.round(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

// 1.7 → "1.7%", 2 → "2%"
function formatPct(n) {
  return `${Number(n.toFixed(1))}%`;
}

const fillOf = (progress) => `${Math.round(progress * 100)}%`;

function shortenWallet(wallet) {
  return wallet.length > 12
    ? `${wallet.slice(0, 6)}...${wallet.slice(-6)}`.toUpperCase()
    : wallet.toUpperCase();
}

function genReference() {
  return "WD-" + Math.random().toString(36).slice(2, 10).toUpperCase();
}

function StatusBadge({ tone, children }) {
  const toneClass = styles[`badge${tone[0].toUpperCase()}${tone.slice(1)}`];
  return (
    <span className={`${styles.badge} ${toneClass}`}>
      <span className={styles.badgeDot} aria-hidden="true" />
      {children}
    </span>
  );
}

function NextSteps({ doneCount }) {
  const { t } = useLanguage();
  const steps = [
    { titleKey: "port_next_verify_title", descKey: "port_next_verify_desc" },
    { titleKey: "port_next_withdraw_title", descKey: "port_next_withdraw_desc" },
    { titleKey: "port_next_trade_title", descKey: "port_next_trade_desc" },
  ];

  return (
    <section className={`${styles.section} ${styles.nextStepsSection}`}>
      <div className={styles.sectionHeader}>
        <p className={styles.sectionLabel}>{t("port_next_steps")}</p>
        <StatusBadge tone="danger">{t("port_pending")}</StatusBadge>
      </div>
      <ol className={styles.nextSteps}>
        {steps.map((step, i) => {
          const done = i < doneCount;
          return (
            <li key={step.titleKey} className={styles.nextStep}>
              <div className={styles.nextStepTrack}>
                <span className={`${styles.nextStepBox} ${done ? styles.nextStepBoxDone : ""}`}>
                  {done && <Check size={20} strokeWidth={2.4} />}
                </span>
                {i < steps.length - 1 && <span className={styles.nextStepLine} aria-hidden="true" />}
              </div>
              <p className={styles.nextStepTitle}>{t(step.titleKey)}</p>
              <p className={styles.nextStepDesc}>{t(step.descKey)}</p>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

// state: "required" | "pending" (submitted, in review) | "failed" (can resubmit)
//   | "rejected" (final, support only)
function KycCard({ state = "required", onVerify, onContactSupport }) {
  const { t } = useLanguage();

  if (state === "pending") {
    return (
      <>
        <p className={styles.sectionLabel}>{t("port_kyc_title")}</p>
        <article className={`${styles.sideCard} ${styles.kycCard}`}>
          <p className={styles.tileLabel}>{t("port_kyc_pending")}</p>
          <p className={styles.kycErrorDesc}>{t("port_kyc_pending_desc")}</p>
        </article>
      </>
    );
  }

  if (state === "failed" || state === "rejected") {
    const final = state === "rejected";
    return (
      <>
        <p className={styles.sectionLabel}>{t("port_kyc_title")}</p>
        <article className={`${styles.sideCard} ${styles.kycCard}`}>
          <p className={`${styles.tileLabel} ${styles.kycErrorLabel}`}>{t("port_kyc_error")}</p>
          <p className={styles.kycErrorDesc}>{t(final ? "port_kyc_rejected_desc" : "port_kyc_error_desc")}</p>
          {!final && (
            <button type="button" className={styles.startBtn} onClick={onVerify}>
              {t("port_kyc_try_again")} <ArrowRight size={18} strokeWidth={2.4} />
            </button>
          )}
          <button type="button" className={styles.kycSupportLink} onClick={onContactSupport}>
            {t("port_kyc_contact_support")}
          </button>
        </article>
      </>
    );
  }

  return (
    <>
      <p className={styles.sectionLabel}>{t("port_kyc_title")}</p>
      <article className={`${styles.sideCard} ${styles.kycCard}`}>
        <p className={styles.tileLabel}>{t("port_kyc_required")}</p>
        <ol className={styles.kycDocs}>
          <li>{t("port_kyc_doc_id")}</li>
          <li>{t("port_kyc_doc_address")}</li>
        </ol>
        <button type="button" className={styles.startBtn} onClick={onVerify}>
          {t("port_kyc_start")} <ArrowRight size={18} strokeWidth={2.4} />
        </button>
      </article>
    </>
  );
}

const WITHDRAW_STEPS = [
  { n: 1, key: "ref_stepper_request" },
  { n: 2, key: "ref_stepper_wallet" },
  { n: 3, key: "ref_stepper_review" },
];

function WithdrawStepper({ step }) {
  const { t } = useLanguage();
  return (
    <ol className={styles.wdStepper}>
      {WITHDRAW_STEPS.map(({ n, key }) => (
        <li key={n} className={`${styles.wdStep} ${step === n ? styles.wdStepActive : ""}`}>
          <span className={styles.wdStepBox}>{n}</span>
          <span className={styles.wdStepLabel}>{t(key)}</span>
        </li>
      ))}
    </ol>
  );
}

// `request` posts the withdrawal to the API (null in the state-switcher previews, which
// only step through the screens); `existing` is the trader's earlier request, if any,
// shown in place of the form.
function WithdrawProfitCard({ onSubmitted, request, existing }) {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [wallet, setWallet] = useState("");
  const [previewReference] = useState(genReference);
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const usdc = QUALIFICATION_PROFIT_PRP * PRP_RATE;
  const netUsdc = usdc - usdc * PROCESSING_FEE_RATE;
  const shortWallet = shortenWallet(wallet);
  const done = submitted || existing;
  const shownStep = done ? 4 : step;

  const submit = async () => {
    if (!request) {
      setStep(4);
      onSubmitted();
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await request({ kind: "qualification", address: wallet.trim() });
    setSubmitting(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setSubmitted(result.withdrawal);
    onSubmitted();
  };

  return (
    <>
      <p className={styles.sectionLabel}>{t("port_withdraw_profit")}</p>
      <article className={`${styles.sideCard} ${styles.withdrawCard}`}>
        {shownStep < 4 && <WithdrawStepper step={shownStep} />}

        {shownStep === 1 && (
          <>
            <h3 className={styles.wdTitle}>{t("port_payout_modal_title")}</h3>
            <div className={styles.wdAmountBox}>
              <div className={styles.wdAmountRow}>
                <span className={styles.tileLabel}>
                  {t("port_to_withdraw")} <InfoTooltip text={t("port_tooltip_to_withdraw")} />
                </span>
                <strong className={styles.wdAmount}>
                  {QUALIFICATION_PROFIT_PRP} <span className={styles.wdAmountUnit}>PRP</span>
                </strong>
              </div>
              <div className={styles.wdRateRow}>
                <div>
                  <span className={styles.wdRateLabel}>PRP</span>
                  <span className={styles.wdRateValue}>1</span>
                </div>
                <ArrowLeftRight size={22} className={styles.wdRateIcon} />
                <div className={styles.wdRateRight}>
                  <span className={styles.wdRateLabel}>USDC</span>
                  <span className={styles.wdRateValue}>={PRP_RATE.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <button type="button" className={styles.startBtn} onClick={() => setStep(2)}>
              {t("ref_continue_btn")}
            </button>
            <p className={styles.wdNote}>{t("port_max_withdraw_note")}</p>
          </>
        )}

        {shownStep === 2 && (
          <>
            <h3 className={styles.wdTitle}>{t("port_payout_modal_title")}</h3>
            <div className={styles.wdReceive}>
              <span className={styles.tileLabel}>{t("port_you_receive")}</span>
              <strong className={styles.wdAmount}>
                ${usdc} <span className={styles.wdAmountUnit}>USDC</span>
              </strong>
              <span className={styles.wdReceiveNote}>
                {t("port_redemption_note")
                  .replace("{prp}", QUALIFICATION_PROFIT_PRP)
                  .replace("{rate}", PRP_RATE.toFixed(2))}
              </span>
            </div>
            <label className={styles.wdFieldLabel} htmlFor="wd-wallet">{t("ref_wallet_label")}</label>
            <input
              id="wd-wallet"
              className={styles.wdInput}
              type="text"
              placeholder={t("port_paste_address")}
              value={wallet}
              onChange={(e) => setWallet(e.target.value)}
            />
            <button
              type="button"
              className={styles.startBtn}
              onClick={() => setStep(3)}
              disabled={!wallet.trim()}
            >
              {t("ref_continue_btn")}
            </button>
            <button type="button" className={styles.wdBackBtn} onClick={() => setStep(1)}>
              {t("ref_back_btn")}
            </button>
          </>
        )}

        {shownStep === 3 && (
          <>
            <h3 className={styles.wdTitle}>{t("ref_review_title")}</h3>
            <dl className={styles.wdReview}>
              <div>
                <dt>{t("port_payout_request_label")}</dt>
                <dd>{QUALIFICATION_PROFIT_PRP} PRP</dd>
              </div>
              <div>
                <dt>{t("port_payout_rate_label")}</dt>
                <dd>1 PRP = {PRP_RATE.toFixed(2)} USDC</dd>
              </div>
              <div>
                <dt>{t("port_usdc_redeemed_label")}</dt>
                <dd>{usdc} USDC</dd>
              </div>
              <div>
                <dt>{t("port_processing_fee_label")}</dt>
                <dd>-{usdc * PROCESSING_FEE_RATE} USDC</dd>
              </div>
              <div>
                <dt>{t("port_net_payout_label")}</dt>
                <dd className={styles.wdReviewAccent}>{netUsdc} USDC</dd>
              </div>
              <div>
                <dt>{t("port_erc20_label")}</dt>
                <dd>{shortWallet}</dd>
              </div>
            </dl>
            <dl className={`${styles.wdReview} ${styles.wdReviewProcessing}`}>
              <div>
                <dt>{t("ref_processing_label")}</dt>
                <dd>{t("ref_processing_time")}</dd>
              </div>
            </dl>
            <button type="button" className={styles.startBtn} onClick={submit} disabled={submitting}>
              {t("port_confirm_payout_btn")} <ArrowRight size={18} strokeWidth={2.4} />
            </button>
            {error && <p className={`${styles.wdNote} ${styles.payoutAmountError}`} role="alert">{error}</p>}
            <button type="button" className={styles.wdBackBtn} onClick={() => setStep(2)} disabled={submitting}>
              {t("ref_back_btn")}
            </button>
          </>
        )}

        {shownStep === 4 && (
          <div className={styles.wdSuccess} role="status">
            <span className={styles.wdSuccessIcon}>
              <Check size={30} strokeWidth={2.2} />
            </span>
            <h3 className={styles.wdTitle}>{t("port_payout_submitted_title")}</h3>
            <p className={styles.wdSuccessText}>{t("port_payout_review_note")}</p>
            <dl className={styles.wdSuccessBox}>
              <div>
                <dt>{t("ref_amount_field")}</dt>
                <dd className={styles.wdReviewAccent}>{done ? formatNumber(done.usdc) : usdc} USDC</dd>
              </div>
              <div>
                <dt>{t("ref_reference_label")}</dt>
                <dd>{done ? done.reference : previewReference}</dd>
              </div>
            </dl>
            <p className={styles.wdSuccessEmail}>
              <span className={styles.badgeDot} aria-hidden="true" />
              {t("ref_email_sent")}
            </p>
          </div>
        )}
      </article>
    </>
  );
}

// Funded traders pick an amount, add a wallet, then confirm; bottom sheet on mobile
// `request` posts the payout to the API; null in the state-switcher previews
function RequestPayoutModal({ onClose, request }) {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [amountText, setAmountText] = useState("");
  const [amountError, setAmountError] = useState("");
  const [wallet, setWallet] = useState("");
  const [previewReference] = useState(genReference);
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const amount = Number(amountText.replace(/,/g, "")) || 0;
  const usdc = amount * PRP_RATE;
  const fee = usdc * PROCESSING_FEE_RATE;
  const netUsdc = usdc - fee;

  const validateAmount = (value) => {
    if (value < PAYOUT_MIN_PRP) {
      return t("port_prop_min_error").replace("{min}", formatNumber(PAYOUT_MIN_PRP));
    }
    if (value > PAYOUT_AVAILABLE_PRP) return t("port_prop_max_error");
    return "";
  };

  const onAmountChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 12);
    const value = Number(digits);
    setAmountText(digits ? formatNumber(value) : "");
    // Once an error is showing, re-check live so it clears as soon as the value is valid
    if (amountError) setAmountError(digits ? validateAmount(value) : "");
  };

  const continueFromAmount = () => {
    const error = validateAmount(amount);
    setAmountError(error);
    if (!error) setStep(2);
  };

  const confirm = async () => {
    if (!request) {
      setStep(4);
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await request({ kind: "funded", amount, address: wallet.trim() });
    setSubmitting(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setSubmitted(result.withdrawal);
    setStep(4);
  };

  return createPortal(
    <div className={`${styles.mt5Overlay} ${styles.payoutOverlay}`} onMouseDown={onClose}>
      <div
        className={`${styles.mt5Modal} ${styles.payoutModal}`}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t("port_payout_modal_title")}
      >
        <span className={styles.payoutHandle} aria-hidden="true" />
        <button
          type="button"
          className={`${styles.mt5Close} ${styles.payoutClose}`}
          onClick={onClose}
          aria-label="Close"
        >
          <X size={24} />
        </button>

        {step < 4 && <WithdrawStepper step={step} />}

        {step === 1 && (
          <>
            <h3 className={styles.wdTitle}>{t("port_payout_modal_title")}</h3>
            <div className={styles.wdAmountBox}>
              <div className={`${styles.wdAmountRow} ${styles.payoutAvailableRow}`}>
                <span className={styles.tileLabel}>
                  {t("port_available_withdraw")} <InfoTooltip text={t("port_tooltip_to_withdraw")} />
                </span>
                <strong className={styles.wdAmount}>
                  {formatNumber(PAYOUT_AVAILABLE_PRP)} <span className={styles.wdAmountUnit}>PRP</span>
                </strong>
              </div>
              <div className={styles.wdRateRow}>
                <div>
                  <span className={styles.wdRateLabel}>PRP</span>
                  <span className={styles.wdRateValue}>1</span>
                </div>
                <ArrowLeftRight size={22} className={styles.wdRateIcon} />
                <div className={styles.wdRateRight}>
                  <span className={styles.wdRateLabel}>USDC</span>
                  <span className={styles.wdRateValue}>={PRP_RATE.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <label className={styles.wdFieldLabel} htmlFor="payout-amount">
              {t("port_prop_amount_label").replace("$PROP", "PRP")}
            </label>
            <div className={`${styles.payoutAmountField} ${amountError ? styles.payoutAmountFieldError : ""}`}>
              <input
                id="payout-amount"
                className={styles.payoutAmountInput}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder={t("port_prop_min_placeholder").replace("{min}", PAYOUT_MIN_PRP)}
                value={amountText}
                onChange={onAmountChange}
                onKeyDown={(e) => { if (e.key === "Enter" && amountText) continueFromAmount(); }}
                aria-invalid={Boolean(amountError)}
                aria-describedby="payout-amount-hint"
              />
              {!amountText && <span className={styles.payoutAmountUnit}>PRP</span>}
            </div>
            <div id="payout-amount-hint" className={styles.payoutAmountHint}>
              <span className={styles.payoutAmountError} role={amountError ? "alert" : undefined}>
                {amountError}
              </span>
              <span>= {amountText ? formatNumber(usdc) : "–"} USDC</span>
            </div>
            <button
              type="button"
              className={styles.startBtn}
              onClick={continueFromAmount}
              disabled={!amountText}
            >
              {t("ref_continue_btn")}
            </button>
            <p className={styles.wdNote}>{t("port_prop_min_note")}</p>
          </>
        )}

        {step === 2 && (
          <>
            <h3 className={styles.wdTitle}>{t("port_payout_modal_title")}</h3>
            <div className={styles.wdReceive}>
              <span className={styles.tileLabel}>{t("port_you_receive")}</span>
              <strong className={styles.wdAmount}>
                ${formatNumber(usdc)} <span className={styles.wdAmountUnit}>USDC</span>
              </strong>
              <span className={styles.wdReceiveNote}>
                {t("port_redemption_note")
                  .replace("{prp}", formatNumber(amount))
                  .replace("{rate}", PRP_RATE.toFixed(2))}
              </span>
            </div>
            <label className={styles.wdFieldLabel} htmlFor="payout-wallet">{t("ref_wallet_label")}</label>
            <input
              id="payout-wallet"
              className={styles.wdInput}
              type="text"
              autoComplete="off"
              placeholder={t("port_paste_address")}
              value={wallet}
              onChange={(e) => setWallet(e.target.value)}
            />
            <button
              type="button"
              className={styles.startBtn}
              onClick={() => setStep(3)}
              disabled={!wallet.trim()}
            >
              {t("ref_continue_btn")}
            </button>
            <button type="button" className={styles.wdBackBtn} onClick={() => setStep(1)}>
              {t("ref_back_btn")}
            </button>
          </>
        )}

        {step === 3 && (
          <>
            <h3 className={styles.wdTitle}>{t("ref_review_title")}</h3>
            <dl className={styles.wdReview}>
              <div>
                <dt>{t("port_payout_request_label")}</dt>
                <dd>{formatNumber(amount)} PRP</dd>
              </div>
              <div>
                <dt>{t("port_payout_rate_label")}</dt>
                <dd>1 PRP = {PRP_RATE.toFixed(2)} USDC</dd>
              </div>
              <div>
                <dt>{t("port_usdc_redeemed_label")}</dt>
                <dd>{formatNumber(usdc)} USDC</dd>
              </div>
              <div>
                <dt>{t("port_processing_fee_label")}</dt>
                <dd>-{formatNumber(fee)} USDC</dd>
              </div>
              <div>
                <dt>{t("port_net_payout_label")}</dt>
                <dd className={styles.wdReviewAccent}>{formatNumber(netUsdc)} USDC</dd>
              </div>
              <div>
                <dt>{t("port_erc20_label")}</dt>
                <dd>{shortenWallet(wallet.trim())}</dd>
              </div>
            </dl>
            <dl className={`${styles.wdReview} ${styles.wdReviewProcessing}`}>
              <div>
                <dt>{t("ref_processing_label")}</dt>
                <dd>{t("ref_processing_time")}</dd>
              </div>
            </dl>
            <button type="button" className={styles.startBtn} onClick={confirm} disabled={submitting}>
              {t("port_confirm_payout_btn")} <ArrowRight size={18} strokeWidth={2.4} />
            </button>
            {error && <p className={`${styles.wdNote} ${styles.payoutAmountError}`} role="alert">{error}</p>}
            <button type="button" className={styles.wdBackBtn} onClick={() => setStep(2)} disabled={submitting}>
              {t("ref_back_btn")}
            </button>
          </>
        )}

        {step === 4 && (
          <div className={styles.wdSuccess} role="status">
            <span className={styles.wdSuccessIcon}>
              <Check size={30} strokeWidth={2.2} />
            </span>
            <h3 className={styles.wdTitle}>{t("port_payout_submitted_title")}</h3>
            <p className={styles.wdSuccessText}>{t("port_payout_submitted_subtitle")}</p>
            <dl className={styles.wdSuccessBox}>
              <div>
                <dt>{t("ref_amount_field")}</dt>
                <dd className={styles.wdReviewAccent}>{formatNumber(submitted ? submitted.netUsdc : netUsdc)} USDC</dd>
              </div>
              <div>
                <dt>{t("ref_reference_label")}</dt>
                <dd>{submitted ? submitted.reference : previewReference}</dd>
              </div>
            </dl>
            <p className={styles.wdSuccessEmail}>
              <span className={styles.badgeDot} aria-hidden="true" />
              {t("ref_email_sent")}
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// Disabled buttons don't fire mouse events, so the wrapper owns hover/focus for the tooltip
function PayoutBelowMinButton({ tooltip }) {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  return (
    <div
      className={styles.payoutBtnWrap}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
      onClick={() => setVisible((v) => !v)}
    >
      <button
        type="button"
        className={styles.payoutBtn}
        aria-disabled="true"
        aria-describedby="payout-min-tooltip"
      >
        {t("port_request_payout")}
      </button>
      <span
        id="payout-min-tooltip"
        role="tooltip"
        className={`${styles.payoutBtnTooltip} ${visible ? styles.payoutBtnTooltipVisible : ""}`}
      >
        {tooltip}
      </span>
    </div>
  );
}

function HelpCenter({ stacked = false }) {
  const { t } = useLanguage();
  return (
    <>
      <p className={styles.sectionLabel}>{t("port_help_center")}</p>
      <article className={styles.sideCard}>
        {stacked && <p className={styles.helpIntro}>{t("port_help_intro")}</p>}
        <div className={`${styles.helpGrid} ${stacked ? styles.helpGridStacked : ""}`}>
          <a href="https://docs.propfunding.com" target="_blank" rel="noopener noreferrer" className={styles.helpBtn}>
            <FileText size={21} />
            <span>{t("port_docs")}</span>
          </a>
          <a href="https://discord.com/invite/8z37khrMA6" target="_blank" rel="noopener noreferrer" className={styles.helpBtn}>
            <DiscordIcon size={21} color="currentColor" />
            <span>{t("port_discord")}</span>
          </a>
        </div>
      </article>
    </>
  );
}

function PropValueCard() {
  const { t } = useLanguage();
  return (
    <>
      <p className={styles.sectionLabel}>{t("port_prop_value")}</p>
      <article className={styles.sideCard}>
        <strong className={styles.propPrice}>0.50</strong>
        <p className={styles.propUnit}>{t("port_usdc_per_prp")}</p>
      </article>
    </>
  );
}

// First-activation discount shown to free traders before they qualify. The count of uses
// left comes from the API; the limit stands in until it loads.
const FIRST_ACTIVATION_CODE = "FIRST100";
const FIRST_ACTIVATION_LIMIT = 100;

function FirstActivationOfferCard() {
  const { t } = useLanguage();
  const { getPromo } = usePayments();
  const [copied, setCopied] = useState(false);
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getPromo(FIRST_ACTIVATION_CODE).then((res) => {
      if (!cancelled && !res.error) setPromo(res);
    });
    return () => {
      cancelled = true;
    };
  }, [getPromo]);

  const limit = promo?.limit ?? FIRST_ACTIVATION_LIMIT;
  const remaining = promo?.remaining ?? limit;

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(FIRST_ACTIVATION_CODE).then(() => setCopied(true), () => {});
  };

  return (
    <>
      <p className={styles.sectionLabel}>{t("port_offer_title")}</p>
      <article className={`${styles.sideCard} ${styles.offerCard}`}>
        <p className={styles.offerDesc}>{t("port_offer_desc").replace("{code}", FIRST_ACTIVATION_CODE)}</p>
        <div className={styles.offerCodeRow}>
          <input
            className={styles.offerCodeInput}
            type="text"
            readOnly
            value={FIRST_ACTIVATION_CODE}
            aria-label={t("port_offer_title")}
          />
          <button type="button" className={styles.offerCopyBtn} onClick={handleCopy}>
            {copied ? t("port_offer_copied") : t("port_offer_copy")}
          </button>
        </div>
        <p className={styles.offerCount}>
          <span className={styles.offerDot} aria-hidden="true" />
          {t("port_offer_remaining")
            .replace("{left}", remaining)
            .replace("{total}", limit)}
        </p>
      </article>
    </>
  );
}

const firstWithdrawal = [
  { date: "May 01, 2026", amount: "100 USDC", txId: "0x73xlsnej8f2c1d9a4b6e0ej2n" },
];

const monthlyWithdrawals = [
  "May 01, 2026",
  "April 01, 2026",
  "March 01, 2026",
  "February 01, 2026",
  "January 01, 2026",
  "December 01, 2025",
  "November 01, 2025",
  "October 01, 2025",
].map((date, i) => ({ date, amount: "5,000 USDC", txId: `0x73xlsnej8f2c1d9a4b6e${i}ej2n` }));

const HISTORY_PREVIEW_ROWS = 5;

function truncateTxId(txId) {
  return `${txId.slice(0, 10)}..${txId.slice(-4)}`;
}

function WithdrawalHistory({ rows }) {
  const { t } = useLanguage();
  const [showAll, setShowAll] = useState(false);
  const visibleRows = showAll ? rows : rows.slice(0, HISTORY_PREVIEW_ROWS);
  return (
    <section className={styles.section}>
      <p className={styles.sectionLabel}>{t("port_withdrawal_hist")}</p>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t("port_date")}</th>
              <th>{t("port_payout_amount")}</th>
              <th>{t("port_status")}</th>
              <th>{t("port_tx_id")}</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr key={row.txId}>
                <td>{row.date}</td>
                <td>{row.amount}</td>
                <td>
                  <span className={styles.completedBadge}>{t("port_completed")}</span>
                </td>
                <td>
                  <a
                    className={styles.txLink}
                    href={`https://etherscan.io/tx/${row.txId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {truncateTxId(row.txId)}
                    <SquareArrowOutUpRight size={16} aria-label="Open transaction" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {visibleRows.length < rows.length && (
          <button type="button" className={styles.viewAllBtn} onClick={() => setShowAll(true)}>
            {t("port_view_all")} <ArrowRight size={18} strokeWidth={2.2} />
          </button>
        )}
      </div>
    </section>
  );
}

const offers = [
  { Icon: BanknoteArrowDown, titleKey: "port_offer_free_title", descKey: "port_offer_free_desc" },
  { Icon: ChartPie, titleKey: "port_offer_loss_title", descKey: "port_offer_loss_desc" },
  { Icon: Wallet, titleKey: "port_offer_share_title", descKey: "port_offer_share_desc" },
];

const previousCycle = [
  { value: "22%", labelKey: "port_drawdown_reached" },
  { value: "7%", labelKey: "port_daily_loss_reached" },
  { value: "$96,000", labelKey: "port_final_equity" },
];

const PAYOUTS_PREVIEW_ROWS = 6;

function PreviousPayouts({ rows }) {
  const { t } = useLanguage();
  const [showAll, setShowAll] = useState(false);
  const visibleRows = showAll ? rows : rows.slice(0, PAYOUTS_PREVIEW_ROWS);
  return (
    <>
      <p className={styles.sectionLabel}>{t("port_previous_payouts")}</p>
      <article className={`${styles.sideCard} ${styles.payoutListCard}`}>
        <ul className={styles.payoutList}>
          {visibleRows.map((row) => (
            <li key={row.txId}>
              <span className={styles.payoutListAmount}>
                <CircleCheck size={16} className={styles.payoutListIcon} aria-hidden="true" />
                {row.amount}
              </span>
              <span>{row.date}</span>
            </li>
          ))}
        </ul>
        {visibleRows.length < rows.length && (
          <button type="button" className={styles.viewAllBtn} onClick={() => setShowAll(true)}>
            {t("port_view_all_payouts")} <ArrowRight size={18} strokeWidth={2.2} />
          </button>
        )}
      </article>
    </>
  );
}

// Funded account that breached its limits: recap the cycle and offer a new account
function CycleEndedView({ onGetFunded }) {
  const { t } = useLanguage();
  return (
    <section className={styles.page}>
      <h1 className={styles.heroTitle}>{t("port_title")}</h1>

      <div className={styles.fundedLayout}>
        <div className={styles.fundedMain}>
          <section className={`${styles.section} ${styles.cycleSection}`}>
            <div className={styles.cycleBanner} role="alert">
              <div className={styles.cycleMain}>
                <h2 className={styles.breachHeading}>{t("port_cycle_ended_title")}</h2>
                <p className={styles.breachDesc}>{t("port_cycle_ended_desc")}</p>
                <p className={styles.promoLine}>
                  <Tag size={20} aria-hidden="true" />
                  <span>
                    {t("port_promo_prefix")} <strong className={styles.promoCode}>RETRY</strong>{" "}
                    {t("port_promo_new_account")}
                  </span>
                </p>
                <button type="button" className={`${styles.retryBtn} ${styles.cycleBtn}`} onClick={onGetFunded}>
                  {t("port_get_funded_again")} <ArrowRight size={20} strokeWidth={2.4} />
                </button>
              </div>
              <div className={styles.cycleStats}>
                <p className={styles.tileLabel}>{t("port_previous_cycle")}</p>
                <dl>
                  {previousCycle.map((stat) => (
                    <div key={stat.labelKey}>
                      <dt>{t(stat.labelKey)}</dt>
                      <dd>{stat.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <p className={styles.sectionLabel}>{t("port_what_we_offer")}</p>
            <div className={styles.offerGrid}>
              {offers.map(({ Icon, titleKey, descKey }) => (
                <article key={titleKey} className={styles.offerCard}>
                  <Icon size={28} strokeWidth={1.6} className={styles.offerIcon} aria-hidden="true" />
                  <h3 className={styles.offerTitle}>{t(titleKey)}</h3>
                  <p className={styles.offerDesc}>{t(descKey)}</p>
                </article>
              ))}
            </div>
          </section>
        </div>

        <aside className={`${styles.sidebar} ${styles.fundedSidebar}`}>
          <div className={styles.sideGroup}>
            <PreviousPayouts rows={monthlyWithdrawals} />
          </div>
          <div className={styles.sideGroup}>
            <HelpCenter stacked />
          </div>
        </aside>
      </div>
    </section>
  );
}

// stage: "activated" (KYC pending) | "verified" (first 2% withdrawal)
// | "funded" (post-withdrawal, fresh account) | "established" (trading history + payouts)
// payoutState: "eligible" | "before-date" (no button yet) | "below-min" (disabled + tooltip)
// live: a real account, so `metrics` (null until the first reading) and `rank` replace the
// sample figures. The state-switcher previews pass live=false.
function FundedView({
  stage,
  payoutState = "eligible",
  kycState = "required",
  onVerify,
  onContactSupport,
  live = false,
  metrics,
  rank,
}) {
  const { t } = useLanguage();
  const [shareOpen, setShareOpen] = useState(false);
  const [withdrawn, setWithdrawn] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  // Previews only step through the screens; a real account's requests go to the API
  const { withdrawals, request } = useWithdrawals(live);
  const qualificationRequest = withdrawals?.find((w) => w.kind === "qualification" && w.status !== "Rejected");
  // Stable so the modal's Escape listener isn't re-bound on every render
  const closePayout = useCallback(() => setPayoutOpen(false), []);
  const verified = stage === "verified";
  const fresh = stage === "funded";
  const established = stage === "established";
  // Past the 2% withdrawal: MT5 details + withdrawal history instead of next steps
  const funded = fresh || established;
  const hasProgress = verified || established;

  // A live account shows a dash until its first reading has been stored
  const pending = live && !metrics;
  // Rounded to whole PRP first, so the sign matches the figure shown (a loss of 0.4 shows as 0, not -0)
  const profitLoss = live ? Math.round(metrics?.profitLoss ?? 0) : established ? 1500 : fresh ? 0 : 200;
  const balanceText = live
    ? metrics && formatPrp(metrics.balance)
    : fresh ? "10,000" : established ? "57,950" : "10,200";
  const profitText = live
    ? metrics && `${profitLoss > 0 ? "+" : ""}${formatPrp(profitLoss || 0)}`
    : `${profitLoss > 0 ? "+" : ""}${formatNumber(profitLoss)}`;
  // Current drawdown: how far equity sits below the starting balance
  const drawdownPct =
    live && metrics?.deposit ? Math.max(0, ((metrics.deposit - metrics.equity) / metrics.deposit) * 100) : 0;
  const rankText = live ? rank ?? "—" : 10;
  const breached = live && Boolean(metrics?.breach);

  // Accounts reset after activation and after the first withdrawal, so objectives start empty
  const objectives = [
    {
      key: "drawdown",
      label: t("port_max_drawdown"),
      tooltip: t("port_tooltip_max_drawdown"),
      fill: hasProgress ? "25%" : "0%",
      currentText: hasProgress ? `5% ${t("port_reached")}` : "",
      targetText: "20%",
      tone: "danger",
    },
    {
      key: "daily",
      label: t("port_daily_loss"),
      tooltip: t("port_tooltip_daily_loss"),
      fill: hasProgress ? "80%" : "0%",
      currentText: hasProgress ? `4% ${t("port_reached")}` : "",
      targetText: "5%",
      tone: "warning",
    },
    {
      key: "inactivity",
      label: t("port_inactivity"),
      tooltip: t("port_tooltip_inactivity"),
      fill: hasProgress ? "14%" : "0%",
      currentText: hasProgress ? `2 ${t("port_days")}` : "",
      targetText: `14 ${t("port_days")}`,
      tone: "neutral",
    },
  ];

  if (live) {
    const [drawdown, daily, inactivity] = objectives;
    const m = metrics;
    Object.assign(drawdown, {
      fill: m ? fillOf(m.maxDrawdown.progress) : "0%",
      currentText: m
        ? `${formatPct(m.maxDrawdown.pct)} ${t(m.maxDrawdown.breached ? "port_breached" : "port_reached")}`
        : "—",
      targetText: m ? `${m.maxDrawdown.limitPct}%` : drawdown.targetText,
    });
    Object.assign(daily, {
      fill: m ? fillOf(m.dailyLoss.progress) : "0%",
      currentText: m
        ? `${formatPct(m.dailyLoss.pct)} ${t(m.dailyLoss.breached ? "port_breached" : "port_reached")}`
        : "—",
      targetText: m ? `${m.dailyLoss.limitPct}%` : daily.targetText,
      tone: m?.dailyLoss.breached ? "danger" : "warning",
    });
    Object.assign(inactivity, {
      fill: m ? fillOf(m.inactivity.progress) : "0%",
      currentText: m ? `${m.inactivity.days} ${t("port_days")}` : "—",
      targetText: `${m?.inactivity.limit ?? 14} ${t("port_days")}`,
      tone: m?.inactivity.breached ? "danger" : "neutral",
    });
  }

  const doneCount = !verified ? 0 : withdrawn || qualificationRequest ? 2 : 1;

  return (
    <section className={styles.page}>
      <h1 className={styles.heroTitle}>{t("port_title")}</h1>

      <div className={styles.fundedLayout}>
        <div className={styles.fundedMain}>
          {!funded && <NextSteps doneCount={doneCount} />}

          <section className={`${styles.section} ${funded ? styles.overviewFirst : ""}`}>
            <p className={`${styles.sectionLabel} ${styles.overviewLabel}`}>
              {t("port_overview")} <RotateCcw size={16} />
            </p>
            <div className={styles.overviewGrid}>
              <article className={styles.overviewTile}>
                <p className={styles.tileLabel}>
                  {t("port_balance")} <InfoTooltip text={t("port_tooltip_balance")} />
                </p>
                <strong className={styles.tileValue}>
                  {pending ? "—" : balanceText} <span className={styles.tileUnit}>PRP</span>
                </strong>
              </article>
              <article className={styles.overviewTile}>
                <p className={styles.tileLabel}>
                  {t("port_profit_loss")} <InfoTooltip text={t("port_tooltip_profit_loss")} />
                </p>
                <strong
                  className={`${styles.tileValue} ${
                    profitLoss < 0 ? styles.tileNegative : profitLoss > 0 ? styles.tilePositive : ""
                  }`}
                >
                  {pending ? "—" : profitText} <span className={styles.tileUnit}>PRP</span>
                </strong>
              </article>
              <article className={styles.overviewTile}>
                <p className={styles.tileLabel}>
                  {t("port_drawdown")} <InfoTooltip text={t("port_tooltip_drawdown")} />
                </p>
                <strong className={styles.tileValue}>{pending ? "—" : `${drawdownPct.toFixed(2)}%`}</strong>
              </article>
              <article className={styles.overviewTile}>
                <p className={styles.tileLabel}>
                  {t("port_rank")} <InfoTooltip text={t("port_tooltip_rank")} />
                </p>
                <div className={`${styles.rankRow} d-flex align-items-center justify-content-between`}>
                  <strong className={styles.tileValue}>{rankText}</strong>
                  {rankText !== "—" && (
                    <button type="button" className={styles.shareBtn} onClick={() => setShareOpen(true)}>
                      {t("port_share")}
                    </button>
                  )}
                </div>
              </article>
            </div>
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <p className={styles.sectionLabel}>{t("port_trading_obj")}</p>
              {breached ? (
                <StatusBadge tone="danger">{t("port_breached").toUpperCase()}</StatusBadge>
              ) : (
                <StatusBadge tone="success">{t("port_in_range")}</StatusBadge>
              )}
            </div>
            <div className={styles.objectivesGridThree}>
              {objectives.map(({ key, ...obj }) => (
                <ProgressTile key={key} {...obj} />
              ))}
            </div>
          </section>

          {funded && <WithdrawalHistory rows={established ? monthlyWithdrawals : firstWithdrawal} />}

          {verified && (
            <section className={styles.section}>
              <HelpCenter />
            </section>
          )}
        </div>

        <aside className={`${styles.sidebar} ${styles.fundedSidebar}`}>
          <div className={styles.sideAction}>
            {funded && (
              <Mt5DetailsCard>
                <div className={`${styles.accountRow} ${styles.accountRowSpaced}`}>
                  <span className={`${styles.accountLabel} ${styles.accountLabelWrap}`}>
                    {t("port_next_payout")}
                  </span>
                  <span className={styles.accountValue}>MAY 28, 2026</span>
                </div>
                {payoutState === "eligible" && (
                  <button type="button" className={styles.payoutBtn} onClick={() => setPayoutOpen(true)}>
                    {t("port_request_payout")}
                  </button>
                )}
                {payoutState === "below-min" && (
                  <PayoutBelowMinButton
                    tooltip={t("port_payout_min_tooltip").replace("{min}", formatNumber(PAYOUT_MIN_PRP))}
                  />
                )}
              </Mt5DetailsCard>
            )}
            {verified && (
              <WithdrawProfitCard
                onSubmitted={() => setWithdrawn(true)}
                request={live ? request : null}
                existing={qualificationRequest}
              />
            )}
            {stage === "activated" && <KycCard state={kycState} onVerify={onVerify} onContactSupport={onContactSupport} />}
          </div>
          <div className={`${styles.sideGroup} ${funded ? styles.desktopOnly : ""}`}>
            <PropValueCard />
          </div>
          {!verified && (
            <div className={styles.sideGroup}>
              <HelpCenter />
            </div>
          )}
        </aside>
      </div>

      {shareOpen && (
        <ShareModal
          rank={rankText}
          propProfit={live ? formatPrp(profitLoss) : formatNumber(profitLoss)}
          usdcPayouts="$0"
          onClose={() => setShareOpen(false)}
        />
      )}
      {payoutOpen && <RequestPayoutModal onClose={closePayout} request={live ? request : null} />}
    </section>
  );
}

export default function PortfolioPage({
  accountStatus,
  payoutState,
  kycState,
  tradingAccount,
  waitingForAccount = false,
  breachPreview = ["max_drawdown"],
  activationDeadline,
  onStartTrading,
  onActivate,
  onBuyActivation,
  onGetFunded,
  onVerify,
  onContactSupport,
}) {
  const { t } = useLanguage();
  const [activating, setActivating] = useState(false);
  const [startError, setStartError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  // Real figures only for a real account still qualifying; the state-switcher previews
  // (no assigned account, or a later stage) keep their sample numbers
  const live = accountStatus === "trading" && Boolean(tradingAccount);
  // After activation the trader keeps the same account, so its figures stay live too
  // (the later "funded"/"established" stages are previews only for now)
  const fundedLive = ["activated", "verified"].includes(accountStatus) && Boolean(tradingAccount);
  // Keyed by login so a free retry's new account doesn't show the breached one's figures
  const metrics = useAccountMetrics(live || fundedLive, tradingAccount?.login);
  // The trader's own row on the leaderboard (flagged `you` by the API) gives their rank
  const { data: board } = useLeaderboard();

  // Starting (or restarting) a free attempt shows the loading screen until the account
  // is assigned (App holds it there for a minimum time)
  const handleStart = async () => {
    if (activating) return;
    setActivating(true);
    setStartError(null);
    try {
      await onStartTrading();
    } catch (err) {
      // An empty pool isn't an error: the trader is queued and sees the waiting notice
      setStartError(err.code === "POOL_EMPTY" ? null : err.message);
    } finally {
      setActivating(false);
    }
  };

  if (accountStatus === "free") {
    return activating ? (
      <ActivatingView>
        <FreeUserView onStartTrading={handleStart} activating />
      </ActivatingView>
    ) : (
      <FreeUserView
        onStartTrading={handleStart}
        error={startError}
        notice={waitingForAccount ? t("port_waiting_account") : null}
      />
    );
  }

  if (accountStatus === "breached") {
    return <CycleEndedView onGetFunded={onGetFunded} />;
  }

  if (["activated", "verified", "funded", "established"].includes(accountStatus)) {
    return (
      <FundedView
        stage={accountStatus}
        payoutState={payoutState}
        kycState={kycState}
        onVerify={onVerify}
        onContactSupport={onContactSupport}
        live={fundedLive}
        metrics={metrics}
        rank={board?.traders?.find((trader) => trader.you)?.rank}
      />
    );
  }

  // A live account fails as soon as the tracker records a breach; the last free attempt
  // failing leaves only the paid activation. Previews use the switcher's rules.
  const liveFailedRules = (live && metrics?.failedRules) || [];
  const liveFailed = liveFailedRules.length > 0;
  // A live account qualifies once the tracker sees the target met with no trades open;
  // the API then holds the 48h activation deadline
  const liveActivation = live && !liveFailed ? metrics?.activation : null;
  const qualified = accountStatus === "qualified" || Boolean(liveActivation);
  const deadline = liveActivation ? Date.parse(liveActivation.deadline) : activationDeadline;
  // An expired window ends the attempt; with no free attempts left only the paid
  // activation remains
  const noRetriesLeft = Boolean(liveActivation) && metrics.retriesLeft === 0;
  const exhausted = accountStatus === "exhausted" || (liveFailed && metrics.retriesLeft === 0);
  const failed = accountStatus === "failed" || exhausted || liveFailed;
  const failedRules = liveFailed ? liveFailedRules : breachPreview;
  // Live: their leaderboard position, "—" until they're on the board. Previews keep samples.
  const liveRank = board?.traders?.find((trader) => trader.you)?.rank;
  const rank = live ? liveRank ?? "—" : failed ? 234 : 13;

  // A free retry that hit an empty pool shows the waiting notice where errors go
  const retryMessage = startError ?? (waitingForAccount ? t("port_waiting_account") : null);

  // A live account shows a dash until its first reading has been stored
  const pending = live && !metrics;
  const balance = live ? metrics && formatPrp(metrics.balance) : formatPrp(10000);
  // Rounded to whole PRP first, so the sign matches the figure shown (a loss of 0.4 shows as 0, not -0)
  const profitLoss = Math.round(live ? metrics?.profitLoss ?? 0 : 150);
  const profitText = pending ? "—" : `${profitLoss > 0 ? "+" : ""}${formatPrp(profitLoss || 0)}`;

  const balanceTile = (
    <article key="balance" className={styles.overviewTile}>
      <p className={styles.tileLabel}>
        {t("port_balance")} <InfoTooltip text={t("port_tooltip_balance")} />
      </p>
      <strong className={styles.tileValue}>
        {pending ? "—" : balance} <span className={styles.tileUnit}>PRP</span>
      </strong>
    </article>
  );

  const profitTile = (
    <article key="profit" className={styles.overviewTile}>
      <p className={styles.tileLabel}>
        {t("port_profit_loss")} <InfoTooltip text={t("port_tooltip_profit_loss")} />
      </p>
      <strong
        className={`${styles.tileValue} ${profitLoss < 0 ? styles.tileNegative : styles.tilePositive}`}
      >
        {profitText} <span className={styles.tileUnit}>PRP</span>
      </strong>
    </article>
  );

  const rankTile = (
    <article key="rank" className={styles.overviewTile}>
      <p className={styles.tileLabel}>
        {t("port_rank")} <InfoTooltip text={t("port_tooltip_rank")} />
      </p>
      <div className={`${styles.rankRow} d-flex align-items-center justify-content-between`}>
        <strong className={styles.tileValue}>{rank}</strong>
        {rank !== "—" && (
          <button type="button" className={styles.shareBtn} onClick={() => setShareOpen(true)}>
            {t("port_share")}
          </button>
        )}
      </div>
    </article>
  );

  const prpValueTile = (
    <article key="prpValue" className={styles.overviewTile}>
      <p className={styles.tileLabel}>
        {t("port_prp_value")} <InfoTooltip text={t("port_tooltip_prop_value")} />
      </p>
      <strong className={styles.tileValue}>1 PRP = {PRP_RATE.toFixed(2)} USDC</strong>
    </article>
  );

  const progress = {
    inactivity: {
      label: t("port_inactivity"),
      tooltip: t("port_tooltip_inactivity"),
      fill: failed && failedRules.includes("inactivity") ? "100%" : "29%",
      currentText: `${failed && failedRules.includes("inactivity") ? 14 : 2} ${t("port_days")}`,
      targetText: `14 ${t("port_days")}`,
      tone: failed && failedRules.includes("inactivity") ? "danger" : "neutral",
    },
    target: {
      label: t("port_qualifying_target"),
      tooltip: t("port_tooltip_qualifying"),
      fill: qualified ? "100%" : "80%",
      currentText: `${qualified ? "1%" : "0.8%"} ${t("port_achieved")}`,
      targetText: "1%",
      targetIcon: <span aria-hidden="true">🏆</span>,
      tone: "positive",
    },
    drawdown: {
      label: t("port_max_drawdown"),
      tooltip: t("port_tooltip_max_drawdown"),
      fill: failed && failedRules.includes("max_drawdown") ? "100%" : "25%",
      currentText: failed && failedRules.includes("max_drawdown")
        ? `20% ${t("port_breached")}`
        : `5% ${t("port_reached")}`,
      targetText: "20%",
      tone: "danger",
    },
    daily: {
      label: t("port_daily_loss"),
      tooltip: t("port_tooltip_daily_loss"),
      fill: failed && failedRules.includes("daily_loss") ? "100%" : "80%",
      currentText: failed && failedRules.includes("daily_loss")
        ? `5% ${t("port_breached")}`
        : `4% ${t("port_reached")}`,
      targetText: "5%",
      tone: failed && failedRules.includes("daily_loss") ? "danger" : "warning",
    },
    minDays: {
      label: t("port_min_trading_days"),
      tooltip: t("port_tooltip_min_days"),
      fill: "33%",
      currentText: "1",
      targetText: "3",
      tone: "positive",
    },
  };

  if (live) {
    const m = metrics;
    const { inactivity, qualifyingTarget: target, maxDrawdown, dailyLoss, tradingDays } = m || {};
    Object.assign(progress.inactivity, {
      fill: m ? fillOf(inactivity.progress) : "0%",
      currentText: m ? `${inactivity.days} ${t("port_days")}` : "—",
      targetText: `${inactivity?.limit ?? 14} ${t("port_days")}`,
      tone: inactivity?.breached ? "danger" : "neutral",
    });
    Object.assign(progress.target, {
      fill: m ? fillOf(target.progress) : "0%",
      // Shown between 0% and the target: a loss reads 0%, and overshooting reads the target
      currentText: m
        ? `${formatPct(Math.min(Math.max(target.achievedPct, 0), target.targetPct))} ${t("port_achieved")}`
        : "—",
      // The API owns the target; fall back to the preview value until metrics load
      ...(m && Number.isFinite(target.targetPct) ? { targetText: formatPct(target.targetPct) } : {}),
    });
    Object.assign(progress.drawdown, {
      fill: m ? fillOf(maxDrawdown.progress) : "0%",
      currentText: m
        ? `${formatPct(maxDrawdown.pct)} ${t(maxDrawdown.breached ? "port_breached" : "port_reached")}`
        : "—",
    });
    Object.assign(progress.daily, {
      fill: m ? fillOf(dailyLoss.progress) : "0%",
      currentText: m
        ? `${formatPct(dailyLoss.pct)} ${t(dailyLoss.breached ? "port_breached" : "port_reached")}`
        : "—",
      tone: dailyLoss?.breached ? "danger" : "warning",
    });
    Object.assign(progress.minDays, {
      fill: m ? fillOf(tradingDays.progress) : "0%",
      currentText: m ? String(tradingDays.count) : "—",
    });
  }

  const progressTile = (key) => <ProgressTile key={key} {...progress[key]} />;

  // Once qualified (or breached), inactivity and minimum days no longer apply
  const overviewTiles = failed
    ? [
        balanceTile,
        rankTile,
        exhausted || failedRules.includes("inactivity") ? progressTile("inactivity") : prpValueTile,
        ...["target", "drawdown", "daily"].map(progressTile),
      ]
    : qualified
    ? [balanceTile, rankTile, profitTile, ...["target", "drawdown", "daily"].map(progressTile)]
    : [
        balanceTile,
        profitTile,
        rankTile,
        ...["inactivity", "target", "drawdown", "daily", "minDays"].map(progressTile),
      ];

  const page = (
    <section className={styles.page}>
      <h1 className={styles.heroTitle}>{t("port_title")}</h1>

      {qualified && (
        <ActivationBanner
          deadline={deadline}
          onActivate={onActivate}
          onRetry={handleStart}
          onBuy={noRetriesLeft ? onBuyActivation : undefined}
          error={retryMessage}
        />
      )}
      {failed && (exhausted
        ? <ExhaustedBanner failedRules={failedRules} onBuy={onBuyActivation} />
        : <BreachBanner failedRules={failedRules} onRetry={handleStart} error={retryMessage} />)}

      {/* ── Overview ── */}
      <section className={styles.section}>
        <p className={styles.sectionLabel}>{t("port_overview")}</p>
        <div className={`${styles.overviewGrid} ${qualified || failed ? styles.overviewGridThree : ""}`}>
          {overviewTiles}
        </div>
      </section>

      {!failed && (
        <div className={styles.activeLayout}>
          {/* ── How it works ── */}
          <section className={styles.processSection}>
            <p className={styles.sectionLabel}>{t("port_process")}</p>
            <h2 className={styles.processTitle}>
              {t("port_how_it")} <span className={styles.processHighlight}>{t("port_works")}</span>
            </h2>
            <ol className={styles.stepList}>
              {processSteps.map((step) => (
                <li key={step.n} className={styles.step}>
                  <span className={styles.stepNum}>{step.n}</span>
                  <div>
                    <p className={styles.stepTitle}>{t(step.titleKey)}</p>
                    <p className={styles.stepDesc}>{t(step.descKey)}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* ── Sidebar ── */}
          <aside className={styles.sidebar}>
            <Mt5DetailsCard account={tradingAccount} />

            {!qualified && (
              <>
                <p className={styles.sectionLabel}>{t("port_prop_value")}</p>
                <article className={styles.sideCard}>
                  <strong className={styles.propPrice}>0.50</strong>
                  <p className={styles.propUnit}>{t("port_usdc_per_prp")}</p>
                </article>
              </>
            )}

            {/* Shown before and after qualifying: the code is used at activation */}
            <FirstActivationOfferCard />
          </aside>
        </div>
      )}

      {shareOpen && (
        <ShareModal
          rank={rank}
          propProfit={live ? formatPrp(profitLoss) : "150"}
          usdcPayouts="$0"
          onClose={() => setShareOpen(false)}
        />
      )}
    </section>
  );

  return activating ? <ActivatingView>{page}</ActivatingView> : page;
}
